import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useReserveContext } from '../../../contexts/useReserveContext';
import {
  fetchServicesForStaff,
  type ServiceForStaff,
} from '../../../utils/reservation/by-Date/fetchServicesForStaff';
import { fetchAvailableTimesByStaff } from '../../../utils/reservation/by-staff/fetchAvailableTimesByStaff';

import ReserveStepHeader from '../ReserveStepHeader';
import ReserveStepFooter from '../ReserveStepFooter';
import ServiceCategoryAccordion, {
  type ServiceAccordionCategory,
} from '../ServiceCategoryAccordion';
import { useI18n } from '../../../i18n';

const toNumberSafe = (v: unknown): number => {
  const n = Number(String(v ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const money = (n?: number | null) => (n == null ? '-' : `$${Number(n).toFixed(2)}`);

const SelectServiceByStaff: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const isEditing = searchParams.get('edit') === '1';
  const {
    storeId,
    selectedDate,
    selectedStartTime,
    selectedStaffId,
    selectedStaffName,
    selectedServices,
    setSelectedServices,
    setSelectedStartTime,
  } = useReserveContext();

  const [services, setServices] = useState<ServiceForStaff[]>([]);
  const [loading, setLoading] = useState(false);
  const [validationMessage, setValidationMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!storeId) {
      toast.info(t('date.selectStoreFirst'));
      navigate('/reserve/by-staff/store');
      return;
    }
    if (!selectedStaffId) {
      toast.info(t('date.selectStaffFirst'));
      navigate('/reserve/by-staff/staff');
      return;
    }
    if (isEditing && (!selectedDate || !selectedStartTime)) {
      toast.info(t('staff.returnAfterServiceDateTime'));
      navigate('/reserve/by-staff/confirm-reservation');
      return;
    }
  }, [storeId, selectedStaffId, selectedDate, selectedStartTime, isEditing, navigate, t]);

  useEffect(() => {
    const load = async () => {
      if (!storeId || !selectedStaffId) return;
      setLoading(true);
      try {
        const data = await fetchServicesForStaff(storeId, selectedStaffId);
        setServices(data);
      } catch (err) {
        console.error(err);
        toast.error(t('service.loadFailed'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [storeId, selectedStaffId, t]);

  const categoryGroups: ServiceAccordionCategory<ServiceForStaff>[] = useMemo(() => {
    const map = new Map<number | null, ServiceAccordionCategory<ServiceForStaff>>();
    services.forEach((sv) => {
      const key = sv.category_id ?? null;
      if (!map.has(key)) {
        map.set(key, {
          category_id: key,
          category_name: sv.category_name || t('menu.other'),
          color_class: sv.color_class,
          services: [],
        });
      }
      map.get(key)!.services.push(sv);
    });
    return Array.from(map.values());
  }, [services, t]);

  const selectedServiceIds = useMemo(
    () => selectedServices.map((s) => s.serviceId),
    [selectedServices],
  );

  const totalMinutes = useMemo(
    () => selectedServices.reduce((sum, s) => sum + toNumberSafe(s.duration_minutes), 0),
    [selectedServices],
  );

  const totalPrice = useMemo(
    () => selectedServices.reduce((sum, s) => sum + toNumberSafe(s.price), 0),
    [selectedServices],
  );

  const toggleService = (sv: ServiceForStaff) => {
    setValidationMessage(null);
    const exists = selectedServiceIds.includes(sv.service_id);

    if (exists) {
      setSelectedServices(selectedServices.filter((s) => s.serviceId !== sv.service_id));
    } else {
      setSelectedServices([
        ...selectedServices,
        {
          serviceId: sv.service_id,
          serviceName: sv.service_name,
          categoryName: sv.category_name || t('menu.other'),
          price: toNumberSafe(sv.price),
          duration_minutes: toNumberSafe(sv.duration_minutes),
          color_class: sv.color_class,
          staffId: selectedStaffId!,
          staffName: selectedStaffName!,
        },
      ]);
    }
    if (!isEditing) {
      setSelectedStartTime(null);
    }
  };

  const handleNext = async () => {
    if (selectedServices.length === 0) {
      toast.warning(t('date.selectServiceFirst'));
      return;
    }
    if (!isEditing) {
      navigate('/reserve/by-staff/date');
      return;
    }

    if (!storeId || !selectedStaffId || !selectedDate || !selectedStartTime) {
      toast.error(t('service.incomplete'));
      return;
    }

    try {
      const serviceIds = selectedServices
        .map((service) => Number(service.serviceId))
        .filter((serviceId) => Number.isFinite(serviceId) && serviceId > 0);

      const slots = await fetchAvailableTimesByStaff({
        storeId,
        staffId: selectedStaffId,
        date: selectedDate,
        durationMinutes: totalMinutes,
        serviceIds,
      });

      const isCurrentTimeAvailable = slots.some(
        (slot) => slot.time === selectedStartTime && slot.available,
      );

      if (!isCurrentTimeAvailable) {
        const message = t('service.newSelectionUnavailableDetail');
        setValidationMessage(message);
        toast.warning(
          t('service.newSelectionUnavailable'),
        );
        return;
      }

      setValidationMessage(null);
      navigate('/reserve/by-staff/confirm-reservation');
    } catch (error: any) {
      console.error(error);
      toast.error(error?.message || t('service.validateFailed'));
    }
  };

  return (
    <div className="flex flex-col min-h-[70vh] space-y-4">
      <ReserveStepHeader
        title={t('service.chooseTitle')}
        onBack={() =>
          navigate(isEditing ? '/reserve/by-staff/confirm-reservation' : '/reserve/by-staff/staff')
        }
        step={3}
        totalSteps={5}
      />

      {loading ? (
        <p className="text-center text-slate-400 text-sm">{t('service.loading')}</p>
      ) : (
        <ServiceCategoryAccordion
          categories={categoryGroups}
          selectedServiceIds={selectedServiceIds}
          onToggleService={toggleService}
          showDuration
        />
      )}

      {validationMessage ? (
        <div className="rounded-[28px] border border-rose-200 bg-rose-50 p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-rose-100 p-2 text-rose-600">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-rose-700">{t('service.cannotChange')}</div>
              <div className="mt-1 text-sm leading-6 text-rose-600">{validationMessage}</div>
            </div>
          </div>
        </div>
      ) : null}

      <ReserveStepFooter
        summary={t('footer.selectedCountWithMinutes', {
          count: selectedServices.length,
          minutes: totalMinutes,
          total: money(totalPrice),
        })}
        primaryLabel={
          isEditing
            ? t('footer.applyWithMinutes', { minutes: totalMinutes })
            : t('footer.nextWithMinutes', { minutes: totalMinutes })
        }
        onPrimary={handleNext}
        primaryDisabled={selectedServices.length === 0}
      />
    </div>
  );
};

export default SelectServiceByStaff;
